import type { SignupProgramOption } from '@galapagos/shared/registration';
import type { CatalogWords } from './catalog-words';

/**
 * Pure rules for the substance step. They match program keys only by
 * equality, so the client never learns which program a key names (ADR-015).
 */

/** An option with its words, as the substance step shows it. */
export interface WordedProgramOption {
  programKey: string;
  label: string;
  description?: string;
  subOptions?: { value: string; label: string }[];
  /** Accessible name of the sub-option group. */
  subOptionsLabel?: string;
  /** A note shown under the list while the option is selected. */
  selectedNote?: string;
}

const PROGRAMS = 'substance.programs';

/**
 * The served options with their words from the signup catalog, keyed by
 * program key and sub-option value. A line the catalog does not hold for an
 * option (a description, a note) means the option has none.
 */
export function wordedProgramOptions(
  words: CatalogWords,
  options: readonly SignupProgramOption[],
): WordedProgramOption[] {
  return options.map(({ programKey, subOptions }) => {
    const key = `${PROGRAMS}.${programKey}`;
    const line = (field: string) =>
      words.has(`${key}.${field}`) ? words.text(`${key}.${field}`) : undefined;
    const description = line('description');
    const selectedNote = line('selectedNote');
    return {
      programKey,
      label: words.text(`${key}.label`),
      ...(description === undefined ? {} : { description }),
      ...(subOptions?.length
        ? {
            subOptions: subOptions.map(({ value }) => ({
              value,
              label: words.text(`${key}.subOptions.${value}`),
            })),
            subOptionsLabel: words.text(`${key}.subOptionsLabel`),
          }
        : {}),
      ...(selectedNote === undefined ? {} : { selectedNote }),
    };
  });
}

/** The options the sponsor allows, in the content's order. */
export function allowedProgramOptions(
  options: readonly SignupProgramOption[],
  sponsorProgramKeys: readonly string[],
): SignupProgramOption[] {
  const allowed = new Set(sponsorProgramKeys);
  return options.filter((option) => allowed.has(option.programKey));
}

/**
 * Whether a selection can continue: at least one program, and a sub-type for
 * every selected program that offers them.
 */
export function isCompleteProgramSelection(
  options: readonly SignupProgramOption[],
  selection: readonly string[],
): boolean {
  const chosen = options.filter((option) => selection.includes(option.programKey));
  if (chosen.length === 0) return false;
  return chosen.every(
    (option) =>
      !option.subOptions?.length || option.subOptions.some((sub) => selection.includes(sub.value)),
  );
}

/** The notes of the selected options, in order, for the line under the list. */
export function selectedProgramNotes(
  options: readonly WordedProgramOption[],
  selection: readonly string[],
): string[] {
  return options.flatMap((option) =>
    option.selectedNote && selection.includes(option.programKey) ? [option.selectedNote] : [],
  );
}

/** A stored selection, kept only where it still matches the offered options. */
export function keptProgramSelection(
  options: readonly SignupProgramOption[],
  stored: readonly string[] | null,
): string[] {
  if (!stored) return [];
  const offered = new Set(
    options.flatMap((option) => [
      option.programKey,
      ...(option.subOptions ?? []).map((sub) => sub.value),
    ]),
  );
  return stored.filter((value) => offered.has(value));
}

/** What the server's too-young list means for a stored selection. */
export type ProgramFloorOutcome =
  | { kind: 'keep' }
  | { kind: 'trimmed'; selection: string[] }
  | { kind: 'repick' };

/**
 * Applies the server's list of programs above the member's age to a stored
 * selection. Some still open: the listed ones go, with their sub-types, and
 * the rest keep their order. None still open: the member chooses again. The
 * list is the server's verdict, so no age or floor is read here.
 */
export function programFloorOutcome(
  options: readonly SignupProgramOption[],
  selection: readonly string[],
  ineligibleProgramKeys: readonly string[],
): ProgramFloorOutcome {
  const ineligible = new Set(ineligibleProgramKeys);
  if (!selection.some((value) => ineligible.has(value))) return { kind: 'keep' };
  // Without the options a sub-type cannot be told from a program, so a kept
  // value might be a dropped program's sub-type: ask again rather than guess.
  if (options.length === 0) return { kind: 'repick' };
  const droppedValues = new Set(ineligible);
  for (const option of options) {
    if (!ineligible.has(option.programKey)) continue;
    for (const sub of option.subOptions ?? []) droppedValues.add(sub.value);
  }
  const kept = selection.filter((value) => !droppedValues.has(value));
  const programKeys = new Set(options.map((option) => option.programKey));
  if (!kept.some((value) => programKeys.has(value))) return { kind: 'repick' };
  return { kind: 'trimmed', selection: kept };
}

/**
 * The program keys in a selection, in the member's order. The selection also
 * holds sub-type values; only an offered option's own key is a program.
 */
export function selectedProgramKeys(
  options: readonly SignupProgramOption[],
  selection: readonly string[] | null,
): string[] {
  if (!selection) return [];
  const programKeys = new Set(options.map((option) => option.programKey));
  return selection.filter((value) => programKeys.has(value));
}

/**
 * A one-program selection read from any stored selection: the first offered
 * program, and its sub-type when the selection holds one of that program's.
 * Used where the sponsor allows one program at a time.
 */
export function singleProgramSelection(
  options: readonly SignupProgramOption[],
  selection: readonly string[] | null,
): string[] {
  const [programKey] = selectedProgramKeys(options, selection);
  if (programKey === undefined) return [];
  const option = options.find((candidate) => candidate.programKey === programKey);
  const subValue = option?.subOptions?.find((sub) => selection?.includes(sub.value))?.value;
  return subValue === undefined ? [programKey] : [programKey, subValue];
}

/** Choosing a program in a one-program list: it replaces the earlier program and its sub-type. */
export function pickSingleProgram(selection: readonly string[], programKey: string): string[] {
  return selection[0] === programKey ? [...selection] : [programKey];
}

/**
 * Choosing a sub-type in a one-program list: it replaces the earlier sub-type
 * of the chosen program. A value that is not one of that program's sub-types
 * leaves the selection as it was.
 */
export function pickSingleSubOption(
  options: readonly SignupProgramOption[],
  selection: readonly string[],
  value: string,
): string[] {
  const [programKey] = selectedProgramKeys(options, selection);
  const option = options.find((candidate) => candidate.programKey === programKey);
  if (programKey === undefined || !option?.subOptions?.some((sub) => sub.value === value)) {
    return [...selection];
  }
  return [programKey, value];
}
